import { Box, Button, Text } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { StepItem, Steps } from 'components/molecules/Steps'
import { useStep } from 'components/molecules/Steps/hook'
import { useContractContext } from 'contexts/contract'
import { ErrorResponseDTO } from 'models'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { getAgentByCNPJandState } from 'services/agent'
import { EStorage } from 'utils/enums/storage'
import { getFromSessionStorage } from 'utils/sessionStorage'
import usePersistedState from 'utils/usePersistedState'
import FormComplementary from '../Forms/FormComplementaryData'
import FormConsortium from '../Forms/FormConsortium'
import FormContract from '../Forms/FormContract'
import FormCreditor from '../Forms/FormCreditor'
import FormDebtor from '../Forms/FormDebtor'
import { FormFileDataProps } from '../Forms/FormFileData/interfaces'
import FormFinancing from '../Forms/FormFinancing'
import FormThirdGuarantor from '../Forms/FormThirdGuarantor'
import FormVehicle from '../Forms/FormVehicle'
import LoadingModal from './Modal/LoadingModal'
import { UFsToAutocomplete } from './Utils/isAutocomplete'
import { mountContract } from './Utils/mountContract'
import { mountExternalAdditive } from './Utils/mountExternalAdditive'
import { mountUpdateContract } from './Utils/mountUpdateContract'
import { FinalResponse, StepsFormProps, StepsProps } from './interface'
import { schema } from './schema'
import { BottomActions, Form, FormActions } from './styles'

const StepsForm: React.FC<StepsProps> = ({
    temp,
    type,
    dataToEdit,
    onCancel,
    onSubmit,
    fetchContracts
}) => {
    const {
        createContract,
        createExternalAdditive,
        editContract,
        forwardToDetran,
        setTypeContractBoolean,
        setFileData,
        fileData
    } = useContractContext()
    const [currentTemp, setCurrentTemp] = usePersistedState<{
        [index: string]: {
            [index: string]: StepsFormProps
        }
    }>(`${EStorage.CONTRACT_TEMP}`, {}, 'session')

    const { id = '' } = useParams()
    const isNewContractFlow =
        type === 'CREATE' || type === 'EXTERNAL_ADDITIVE'

    const methods = useForm<StepsFormProps>({
        resolver: yupResolver(schema(type)),
        mode: 'onChange',
        shouldUnregister: false,
        defaultValues: temp?.temp
            ? currentTemp[temp.uf]?.[temp.chassi]
            : !isNewContractFlow
            ? dataToEdit
            : {}
    })

    const {
        handleSubmit,
        trigger,
        watch,
        formState,
        getValues,
        setValue,
        setError,
        clearErrors
    } = methods

    const ufContrato = watch('contract.ufContrato')

    const [agentCNPJ, setAgentCNPJ] = useState('')
    const [instituicaoPagaTaxa, setinstituicaoPagaTaxa] = useState(false)
    const isAsyncToDetran = useRef<boolean | null>(false)
    const [fakeError, setfakeError] = useState(false)
    const [errorMensage, setErrorMensage] = useState<
        { codigo: string; mensagem: string }[]
    >([])
    const [contractResponse, setContractResponse] =
        useState<FinalResponse | null>(null)

    const stepsList = [
        {
            label: 'Veículo',
            content: <FormVehicle />,
            onClick: () => setStep(1)
        },
        {
            label: 'Credor',
            content: <FormCreditor setAgentCNPJ={setAgentCNPJ} />,
            onClick: () => setStep(2)
        },
        {
            label: 'Contrato',
            content: <FormContract instituicaoPagaTaxa={instituicaoPagaTaxa} />,
            onClick: () => setStep(3)
        },
        {
            label: 'Complementares',
            content: <FormComplementary />,
            onClick: () => setStep(4)
        },
        {
            label: 'Financiamento',
            content: <FormFinancing />,
            onClick: () => setStep(5)
        },
        {
            label: 'Consórcio',
            content: <FormConsortium />,
            onClick: () => setStep(6)
        },
        {
            label: 'Devedor',
            content: <FormDebtor />,
            onClick: () => setStep(7)
        },
        {
            label: 'Terceiro Garantidor',
            content: <FormThirdGuarantor />,
            onClick: () => setStep(8)
        }
    ]

    const visibleSteps = stepsList.filter(step =>
        ufContrato === 'MS' ? step.label !== 'Anexos' : true
    )

    const [currentStep, handlesStep, untilStep] = useStep(visibleSteps.length, true)
    const { goToNextStep, goToPrevStep, setStep } = handlesStep

    const addModalCheck = useRef<ModalBuildHandle>(null)
    const addModalToClose = useRef<ModalBuildHandle>(null)

    const failAsyncDetran = (): void => {
        isAsyncToDetran.current = false
    }

    const successAsyncDetran = (): void => {
        isAsyncToDetran.current = true
    }

    const correctionContract = (): void => {
        failAsyncDetran()
        setErrorMensage([])
        setContractResponse(null)
        setfakeError(false)
        setStep(1)
    }

    const handleModalClose = (): void => {
        addModalCheck.current?.onClose()
        setFileData({} as FormFileDataProps)
        setErrorMensage([])
        setContractResponse(null)
    }

    const handleAddModalOpen = (): void => {
        setContractResponse(null)
        addModalCheck.current?.onOpen()
    }

    const handleModalCloseAll = (): void => {
        addModalCheck.current?.onClose()
        setFileData({} as FormFileDataProps)
        setErrorMensage([])
        setContractResponse(null)
        onCancel && onCancel()
    }

    const handleCloseAllFlow = (): void => {
        setFileData({} as FormFileDataProps)
        setErrorMensage([])
        setContractResponse(null)
        onSubmit && onSubmit()
    }

    const handleFailBuildContratc = (
        data: { codigo: string; mensagem: string }[]
    ) => {
        successAsyncDetran()
        setfakeError(true)
        setContractResponse(null)
        setErrorMensage(data)
    }

    const fetchAgent = async () => {
        const query = {
            cnpj: agentCNPJ,
            uf: watch('contract.ufContrato')
        }

        const response = await getAgentByCNPJandState({ ...query })
        const instituicaoPagaTaxa = response?.data?.instituicaoPagaTaxa
        setinstituicaoPagaTaxa(instituicaoPagaTaxa ?? false)
    }

    const handleNext = async (
        e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let valid = false
        const isLastStep = currentStep === visibleSteps.length

        switch (currentStep) {
            case 1:
                valid = await trigger(['vehicles', 'vehiclesList'])
                e?.preventDefault()
                break
            case 2:
                valid = await trigger('creditor')
                e?.preventDefault()
                break
            case 3:
                valid = await trigger('contract')
                if (type === 'EXTERNAL_ADDITIVE') {
                    const numeroAditivo = Number(
                        watch('contract.numAditivoContratoOrigem')
                    )

                    if (!Number.isInteger(numeroAditivo) || numeroAditivo <= 0) {
                        setError('contract.numAditivoContratoOrigem', {
                            type: 'manual',
                            message: 'Informe um número de aditivo maior que zero'
                        })
                        valid = false
                    } else {
                        clearErrors('contract.numAditivoContratoOrigem')
                    }
                }
                e?.preventDefault()
                break
            case 4:
                valid = await trigger('complementary')
                e?.preventDefault()
                break
            case 5:
                valid = await trigger('financing')
                e?.preventDefault()
                break
            case 6:
                valid = await trigger('consortium')
                e?.preventDefault()
                break
            case 7:
                valid = await trigger('debtor')
                e?.preventDefault()
                break
            case 8:
                valid = await trigger('terceiroGarantidor')
                if (!isLastStep) {
                    e?.preventDefault()
                }
                break
        }
        if (valid) {
            if (currentStep === 2) {
                await fetchAgent()
            }
            if (!isLastStep) {
                goToNextStep()
            }
        }
    }

    const handlePrev = () => {
        currentStep === 1 ? onCancel && onCancel() : goToPrevStep()
    }

    const handleCancelWithModal = () => {
        addModalToClose.current?.onOpen()
    }

    const cancelTheOperationYes = () => {
        addModalToClose.current?.onClose()
        onCancel && onCancel()
    }

    const cancelTheOperationNo = () => {
        addModalToClose.current?.onClose()
    }

    const submitContract = (data: StepsFormProps) => {
        data.complementary.correcaoMonetaria &&
            (data.complementary.correcaoMonetaria = Number(
                data.complementary.correcaoMonetaria
            ))

        switch (type) {
            case 'CREATE':
                handleAddModalOpen()
                createContract(mountContract({ ...data, anexos: fileData }))
                    .then(({ data: res }) => {
                        setContractResponse(res)
                        if (res.url === null || res.url === undefined) {
                            successAsyncDetran()
                        } else {
                            forwardToDetran(res)
                                .then(() => successAsyncDetran())
                                .catch(error => {
                                    const { data } =
                                        error.response as ErrorResponseDTO

                                    handleFailBuildContratc(data)
                                })
                        }
                        fetchContracts &&
                            fetchContracts({
                                page: 1,
                                limit: 10
                            })
                    })
                    .catch(error => {
                        setContractResponse(null)
                        const { data } = error.response as ErrorResponseDTO

                        handleFailBuildContratc(data)
                    })
                break
            case 'EXTERNAL_ADDITIVE':
                if (
                    !Number.isInteger(
                        Number(data.contract.numAditivoContratoOrigem)
                    ) ||
                    Number(data.contract.numAditivoContratoOrigem) <= 0
                ) {
                    setError('contract.numAditivoContratoOrigem', {
                        type: 'manual',
                        message: 'Informe um número de aditivo maior que zero'
                    })
                    setStep(3)
                    return
                }

                handleAddModalOpen()
                createExternalAdditive(
                    mountExternalAdditive({ ...data, anexos: fileData })
                )
                    .then(({ data: res }) => {
                        setContractResponse(res)
                        if (res.url === null || res.url === undefined) {
                            successAsyncDetran()
                        } else {
                            forwardToDetran(res)
                                .then(() => successAsyncDetran())
                                .catch(error => {
                                    const { data } =
                                        error.response as ErrorResponseDTO

                                    handleFailBuildContratc(data)
                                })
                        }
                        fetchContracts &&
                            fetchContracts({
                                page: 1,
                                limit: 10
                            })
                    })
                    .catch(error => {
                        setContractResponse(null)
                        const { data } = error.response as ErrorResponseDTO

                        handleFailBuildContratc(data)
                    })
                break
            case 'UPDATE':
                handleAddModalOpen()
                editContract(
                    id,
                    mountUpdateContract({ ...data, anexos: fileData }),
                    type
                )
                    .then(({ data: res }) => {
                        setContractResponse(res)
                        if (res.url === null || res.url === undefined) {
                            successAsyncDetran()
                        } else {
                            forwardToDetran(res)
                                .then(() => successAsyncDetran())
                                .catch(error => {
                                    const { data } =
                                        error.response as ErrorResponseDTO

                                    handleFailBuildContratc(data)
                                })
                        }
                    })
                    .catch(error => {
                        setContractResponse(null)
                        const { data } = error.response as ErrorResponseDTO

                        handleFailBuildContratc(data)
                    })
                break

            case 'ADDITIVE':
                handleAddModalOpen()
                editContract(
                    id,
                    mountUpdateContract({ ...data, anexos: fileData }),
                    type
                )
                    .then(({ data: res }) => {
                        setContractResponse(res)
                        if (res.url === null || res.url === undefined) {
                            successAsyncDetran()
                        } else {
                            forwardToDetran(res)
                                .then(() => successAsyncDetran())
                                .catch(error => {
                                    const { data } =
                                        error.response as ErrorResponseDTO

                                    handleFailBuildContratc(data)
                                })
                        }
                    })
                    .catch(error => {
                        setContractResponse(null)
                        const { data } = error.response as ErrorResponseDTO

                        handleFailBuildContratc(data)
                    })
                break
            case 'EDITADDITIVE':
                handleAddModalOpen()
                editContract(
                    id,
                    mountUpdateContract({ ...data, anexos: fileData }),
                    type
                )
                    .then(({ data: res }) => {
                        setContractResponse(res)
                        if (res.url === null || res.url === undefined) {
                            successAsyncDetran()
                        } else {
                            forwardToDetran(res)
                                .then(() => successAsyncDetran())
                                .catch(error => {
                                    const { data } =
                                        error.response as ErrorResponseDTO

                                    handleFailBuildContratc(data)
                                })
                        }
                    })
                    .catch(error => {
                        setContractResponse(null)
                        const { data } = error.response as ErrorResponseDTO

                        handleFailBuildContratc(data)
                    })
                break
        }
    }

    useEffect(() => {
        const subscription = watch(async () => {
            type === 'CREATE' &&
                temp &&
                setCurrentTemp({
                    ...currentTemp,
                    [temp.uf]: {
                        ...currentTemp[temp.uf],
                        [temp.chassi]: getValues()
                    }
                })
        })
        return () => subscription.unsubscribe()
    }, [watch])

    const autoCompleteContract = () => {
        const formAutocomplete = getFromSessionStorage('form-autocomplete')
        Object.keys(formAutocomplete).forEach((field: any) => {
            const value = formAutocomplete[field] ?? ''
            setValue(`contract.${field}` as any, value)
        })
    }

    const autoCompleteDebtor = () => {
        const formAutocomplete = getFromSessionStorage('form-autocomplete')
        const debtor = formAutocomplete?.devedor

        if (!debtor) return
        Object.keys(debtor).forEach((field: any) => {
            if (field === 'endereco') {
                setValue(
                    `debtor.endereco.logradouro`,
                    debtor?.endereco?.logradouro ?? ''
                )
                setValue(
                    `debtor.endereco.numero`,
                    debtor?.endereco?.numero ?? ''
                )
                setValue(
                    `debtor.endereco.bairro`,
                    debtor?.endereco?.bairro ?? ''
                )
                setValue(
                    `debtor.endereco.municipio`,
                    debtor?.endereco?.municipio ?? ''
                )
                setValue(`debtor.endereco.uf`, debtor?.endereco?.uf ?? '')
                setValue(`debtor.endereco.cep`, debtor?.endereco?.cep ?? '')
            } else {
                const value = debtor[field] ?? ''
                setValue(`debtor.${field}` as any, value)
            }
        })
    }

    const autoCompleteFinancing = () => {
        const formAutocomplete = getFromSessionStorage('form-autocomplete')
        const financing = {
            vencimentoUltimaParcela:
                formAutocomplete.vencimentoUltimaParcela ?? ''
        }
        setValue(
            `financing.vencimentoUltimaParcela`,
            financing.vencimentoUltimaParcela
        )
    }


    const autoComplete = () => {
        const formAutocomplete = getFromSessionStorage('form-autocomplete')
        if (!formAutocomplete) return
        autoCompleteContract()
        autoCompleteDebtor()
        autoCompleteFinancing()
    }

    useEffect(() => {
        if (isNewContractFlow && temp) {
            if (UFsToAutocomplete.includes(temp.uf)) {
                //autoComplete()
            }
            setValue('contract.ufContrato', temp.uf)
            setValue('creditor.endereco.uf', temp.uf)
            setValue('vehicles.ufGravame', temp.uf)
            setValue('vehicles.chassi', temp.chassi)
        }
    }, [])

    useEffect(() => {
        setTypeContractBoolean(isNewContractFlow ? false : true)
    }, [type])

    return (
        <>
            <FormProvider {...methods}>
                <Form onSubmit={handleSubmit(submitContract)}>
                    <Steps
                        currentStep={currentStep}
                        untilStep={untilStep}
                        borderTopRadius={6}
                        background="primary.700"
                        color="white"
                    >
                        {/* 3. Renderizamos o array filtrado (visibleSteps) */}
                        {visibleSteps.map(({ label, content, onClick }) => (
                            <StepItem
                                aria-label={label}
                                key={label}
                                valid={true}
                                onClick={onClick}
                                marginBottom={6}
                            >
                                {content}
                            </StepItem>
                        ))}
                    </Steps>
                    <FormActions>
                        <Button
                            data-testid="form-action-cancel"
                            colorScheme="primary"
                            variant="outline"
                            onClick={handlePrev}
                            disabled={formState.isSubmitting}
                        >
                            {currentStep === 1 ? 'Cancelar' : 'Voltar'}
                        </Button>
                        {currentStep > 1 && (
                            <Button
                                data-testid="form-action-cancel"
                                colorScheme="primary"
                                variant="outline"
                                onClick={handleCancelWithModal}
                                disabled={formState.isSubmitting}
                            >
                                {'Cancelar'}
                            </Button>
                        )}
                        <Button
                            data-testid="form-action-confirm"
                            colorScheme="primary"
                            type={
                                // 4. Condicional atualizada para o tamanho do array visível
                                currentStep === visibleSteps.length
                                    ? 'submit'
                                    : 'button'
                            }
                            onClick={e => handleNext(e)}
                            isLoading={formState.isSubmitting}
                        >
                            {currentStep === visibleSteps.length
                                ? 'Finalizar'
                                : 'Continuar'}
                        </Button>
                    </FormActions>
                </Form>
            </FormProvider>

            <ModalBuild
                ref={addModalCheck}
                closeOnOverlayClick={false}
                title=" "
                size="5xl"
                onAfterClose={handleModalCloseAll}
                onAfterCloseRefresh={handleCloseAllFlow}
            >
                <LoadingModal
                    onCancelForm={handleModalClose}
                    onInValid={failAsyncDetran}
                    isLoading={isAsyncToDetran.current}
                    toCorrectContract={correctionContract}
                    onSubmit={handleCloseAllFlow}
                    isError={fakeError}
                    typeModal={type}
                    dataError={errorMensage}
                    contractResponse={contractResponse}
                />
            </ModalBuild>

            <ModalBuild
                ref={addModalToClose}
                closeOnOverlayClick={false}
                title=" "
                size="md"
            >
                <Box display="flex" justifyContent="center" mt="1rem">
                    <Text>Deseja cancelar a operação?</Text>
                </Box>
                <BottomActions>
                    <Button
                        data-testid="form-action-cancel"
                        colorScheme="primary"
                        variant="outline"
                        onClick={cancelTheOperationYes}
                        disabled={formState.isSubmitting}
                    >
                        {'Sim'}
                    </Button>
                    <Button
                        data-testid="form-action-cancel"
                        colorScheme="primary"
                        onClick={cancelTheOperationNo}
                    >
                        {'Não'}
                    </Button>
                </BottomActions>
            </ModalBuild>
        </>
    )
}

export default StepsForm
